import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  ArrowLeft, HelpCircle, Save, Plus, 
  Trash2, Edit3, MessageSquare, List, Search 
} from 'lucide-react';
import { baseurl } from '../../baseurl';

const Faqs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminToken = localStorage.getItem('adminToken');
  
  const faqId = searchParams.get('id');
  const isEdit = searchParams.get('action') === 'edit' && faqId;

  const [faqsList, setFaqsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    is_active: true
  });

  const fetchAllFaqs = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllFaqs`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (res.data.success) setFaqsList(res.data.faqs);
    } catch (err) {
      console.error("Error fetching FAQs", err);
    }
  };

  useEffect(() => {
    fetchAllFaqs();
    if (isEdit) {
      const fetchSingleFaq = async () => {
        try {
          const res = await axios.get(`${baseurl}getFaqById/${faqId}`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          if (res.data.success) {
            setFormData({
              question: res.data.faq.question,
              answer: res.data.faq.answer,
              is_active: res.data.faq.is_active
            });
          }
        } catch (err) {
          Swal.fire('Error', 'Could not fetch FAQ details', 'error');
        }
      };
      fetchSingleFaq();
    }
  }, [isEdit, faqId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 Payload being sent:", formData);

    Swal.fire({ title: 'Processing...', didOpen: () => Swal.showLoading() });

    try {
      const url = isEdit ? `${baseurl}updateFaq/${faqId}` : `${baseurl}createFaq`;
      const method = isEdit ? 'put' : 'post';
      
      const res = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (res.data.success) {
        Swal.fire('Success', isEdit ? 'FAQ Updated' : 'FAQ Added', 'success');
        setFormData({ question: '', answer: '', is_active: true });
        if(isEdit) navigate('/faqs');
        fetchAllFaqs();
      }
    } catch (err) {
      console.error("API Error:", err);
      Swal.fire('Error', err.response?.data?.message || 'Action failed.', 'error');
    }
  };

  const deleteFaq = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: "This FAQ will be removed permanently!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4318FF',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await axios.delete(`${baseurl}deleteFaq/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (res.data.success) {
          fetchAllFaqs();
          Swal.fire('Deleted!', 'FAQ has been removed.', 'success');
        }
      } catch (err) {
        Swal.fire('Error', 'Delete failed', 'error');
      }
    }
  };

  const filteredFaqs = faqsList.filter(f => 
    f.question.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app-container" style={{ background: '#F4F7FE', minHeight: '100vh' }}>
      <Topbar />
      <div className="container-fluid p-4">
        
        <div className="row mb-4 align-items-center">
          <div className="col-auto">
            <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm rounded-circle p-2">
              <ArrowLeft size={20} />
            </button>
          </div>
          <div className="col">
            <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>Knowledge Base Manager</h3>
            <p className="text-muted small m-0">Manage your PostgreSQL-backed FAQ system</p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-xl-5 col-lg-6">
            <div className="card border-0 shadow-sm p-4 sticky-top" style={{ borderRadius: '24px', top: '90px', zIndex: 10 }}>
              <div className="d-flex align-items-center mb-4">
                <div className="p-3 rounded-4 me-3" style={{ background: 'rgba(67, 24, 255, 0.1)' }}>
                  <HelpCircle size={24} className="text-primary" />
                </div>
                <div>
                    <h5 className="fw-bold m-0">{isEdit ? 'Edit Question' : 'New Question'}</h5>
                    <span className="text-muted small">Update your knowledge base</span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">The Question</label>
                  <input 
                    type="text" 
                    name="question" 
                    className="form-control border-0 bg-light p-3" 
                    value={formData.question}
                    onChange={handleInputChange}
                    required 
                    style={{ borderRadius: '12px' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">The Answer</label>
                  <textarea 
                    name="answer" 
                    className="form-control border-0 bg-light p-3" 
                    rows="6" 
                    value={formData.answer}
                    onChange={handleInputChange}
                    required
                    style={{ borderRadius: '12px' }}
                  ></textarea>
                </div>

                <div className="mb-4 d-flex align-items-center">
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      name="is_active"
                      id="activeSwitch"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label small fw-bold text-muted ms-2" htmlFor="activeSwitch">
                      VISIBLE TO USERS
                    </label>
                  </div>
                </div>

                <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary rounded-pill fw-bold py-3 shadow-sm border-0" style={{ background: '#4318FF' }}>
                        {isEdit ? <Save size={18} className="me-2"/> : <Plus size={18} className="me-2" />}
                        {isEdit ? 'Update Entry' : 'Publish FAQ'}
                    </button>
                    {isEdit && (
                        <button type="button" onClick={() => navigate('/faqs')} className="btn btn-link text-muted">Discard Changes</button>
                    )}
                </div>
              </form>
            </div>
          </div>

          <div className="col-xl-7 col-lg-6">
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '24px', minHeight: '80vh' }}>
              <div className="row align-items-center mb-4 g-3">
                <div className="col-md-6">
                    <div className="d-flex align-items-center">
                        <div className="p-2 rounded-3 me-2" style={{ background: '#F4F7FE' }}>
                            <List size={20} className="text-primary" />
                        </div>
                        <h5 className="fw-bold m-0">Live FAQs ({filteredFaqs.length})</h5>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="position-relative">
                        <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                        <input 
                            type="text" 
                            className="form-control border-0 bg-light ps-5 rounded-pill" 
                            placeholder="Search questions..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
              </div>

              <div className="faq-scroll-container">
                {filteredFaqs.length === 0 ? (
                  <div className="text-center py-5">
                    <MessageSquare size={48} className="text-light mb-3" />
                    <p className="text-muted">No entries found.</p>
                  </div>
                ) : (
                    filteredFaqs.map((faq, index) => (
                    <div key={faq.id || index} className={`faq-item-card mb-3 p-4 border-0 rounded-4 ${!faq.is_active ? 'opacity-50' : ''}`}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-dark pe-3 mb-0">{faq.question}</h6>
                        <div className="d-flex gap-2">
                          <button onClick={() => navigate(`?action=edit&id=${faq.id}`)} className="btn btn-sm btn-white text-primary rounded-circle shadow-sm">
                            <Edit3 size={14} />
                          </button>
                          <button onClick={() => deleteFaq(faq.id)} className="btn btn-sm btn-white text-danger rounded-circle shadow-sm">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="small text-muted mb-0 mt-2">{faq.answer}</p>
                      {!faq.is_active && <span className="badge bg-secondary mt-2" style={{fontSize: '9px'}}>HIDDEN</span>}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .faq-item-card { background: #fff; border: 1px solid #f1f1f1 !important; transition: all 0.2s ease; }
        .faq-item-card:hover { transform: translateY(-3px); border-color: #4318FF !important; box-shadow: 0 12px 24px rgba(67, 24, 255, 0.05); }
        .faq-scroll-container { max-height: calc(100vh - 250px); overflow-y: auto; }
        .btn-white { background: #fff; border: 1px solid #f1f1f1; }
      `}</style>
    </div>
  );
};

export default Faqs;